import { useState } from 'react';
import { MenuItem, OutlinedInput, Select, SxProps } from '@mui/material';
import clsx from 'clsx';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import { Icon, IconButton } from '@atom/mui';
import { DeleteIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  PciModuleDistress,
  PciModuleInspectionOptions,
} from '@atom/types/form';
import { Media } from '@atom/types/media';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './pci.module.css';

interface Props {
  formInstanceId: string;
  assetId: string;
  preview: boolean;
  options: PciModuleInspectionOptions;
  distress: PciModuleDistress;
  media: Media[];
  onChange: (property: string, value: any) => void;
  onDelete: () => void;
  removeFile: (mediaId: string, subjectId: string) => void;
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  isEditable: boolean;
}
const selectInputSx: SxProps = {
  padding: '8px 14px',
};

const selectedOptionSx: SxProps = {
  '&.Mui-selected': {
    color: colors.brand.blue,
    backgroundColor: `${colors.neutral.white} !important`,
  },
};

const AcInspectionRow = ({
  options,
  distress,
  onChange,
  onDelete,
  isEditable,
}: Props) => {
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const handleQuantityChange = (event: any) => {
    const { value } = event.target;

    onChange('quantity', value);
  };

  const handleDelete = () => {
    onDelete();
    setDeleteOpen(false);
  };

  return (
    <>
      <div className={classes.tableRow}>
        <div className={clsx(classes.column, classes.distress)}>
          <Select
            variant="outlined"
            fullWidth
            value={distress.distress || ''}
            onChange={event => onChange('distress', event.target.value)}
            slotProps={{
              input: { sx: selectInputSx },
            }}
            displayEmpty
            disabled={!isEditable}
          >
            {options.distress.map(option => (
              <MenuItem
                key={option.name}
                value={option.name}
                className={classes.menuItem}
                sx={selectedOptionSx}
              >
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={clsx(classes.column, classes.severity)}>
          <Select
            variant="outlined"
            fullWidth
            value={distress.severity || ''}
            onChange={event => onChange('severity', event.target.value)}
            slotProps={{
              input: { sx: selectInputSx },
            }}
            disabled={!isEditable}
          >
            {options.severity.map(option => (
              <MenuItem
                key={option.name}
                value={option.name}
                className={classes.menuItem}
                sx={selectedOptionSx}
              >
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={clsx(classes.column, classes.quantity)}>
          <OutlinedInput
            fullWidth
            value={distress.quantity || ''}
            onChange={handleQuantityChange}
            slotProps={{
              input: {
                onKeyPress: event => {
                  if (isNilOrEmpty(R.match(/[0-9]/, event.key))) {
                    event.preventDefault();
                  }

                  if (event.key === '0' && !distress.quantity) {
                    event.preventDefault();
                  }
                },
                className: classes.input,
              },
            }}
            disabled={!isEditable}
          />
        </div>
        <div className={clsx(classes.column, classes.notes)}>
          <OutlinedInput
            fullWidth
            value={distress.notes || ''}
            className={classes.input}
            onChange={event => onChange('notes', event.target.value)}
            multiline
            disabled={!isEditable}
          />
        </div>
        {isEditable && (
          <div className={clsx(classes.column, classes.delete)}>
            <IconButton onClick={() => setDeleteOpen(true)}>
              <Icon icon={DeleteIcon} />
            </IconButton>
          </div>
        )}
      </div>
      <DeleteModal
        title="Remove Distress"
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        content="Are you sure you want to remove this distress?"
      />
    </>
  );
};

export default AcInspectionRow;
